import { ReactElement } from 'react';
import { Flex, Box } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import { Form, useFormik, FormikProvider } from 'formik';
import { useToasts } from 'react-toast-notifications';
import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import useBreadcrumb from 'packages/kakapo-ui-kit/components/breadcrumb/useBreadcrumb';
import {
	PageTitle,
	SubSectionTitle,
	SectionTitle,
} from 'packages/kakapo-ui-kit/components/common/Headings';
import DetailsTableForm from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from 'packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import Button from 'packages/kakapo-ui-kit/components/editors/Button';
import useErrorBoundary from 'packages/kakapo-ui-application/hooks/useErrrorBoundary';
import GraphQLErrorMessage from 'packages/kakapo-ui-kit/components/forms/errors/GraphQLErrorMessage';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import Preview from './components/Preview';
import { Prompt } from 'react-router';
import ActionListItem, {
	ActionArchiveListItem,
} from 'packages/kakapo-ui-kit/components/actions/ActionListItem';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';

function EvaluationFormEditor(props: { form: graphql.EvaluationForm }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();

	const history = useHistory();

	const formik = useFormik({
		initialValues: {
			title: props.form.title,
			description: props.form.description ?? [],
			showAnswersWhenAllUsersShared: props.form.showAnswersWhenAllUsersShared ?? false,
			sections: props.form.sections.map((section, index) => ({
				id: `__${index}`,
				items: section.items.slice(),
			})),
		},
		onSubmit: () => {},
		enableReinitialize: true,
	});

	const [error, attempt, reset] = useErrorBoundary();

	const [update, { loading }] = graphql.useUpdateEvaluationFormMutation();
	const [duplicate, { loading: loadingDuplicate }] = graphql.useCreateEvaluationFormMutation();

	const mapInput = (duplicate = false) => {
		return {
			title: formik.values.title.map((text) => ({
				languageID: text.languageID,
				text: duplicate
					? t('common.copyOf', { text: text.text, lng: text.languageID.split('_')[0] })
					: text.text,
			})),
			showAnswersWhenAllUsersShared: formik.values.showAnswersWhenAllUsersShared ?? false,
			description: formik.values.description,
			sections: formik.values.sections.map((section) => ({
				items: section.items.map((item) => ({
					id: duplicate ? undefined : item.id.startsWith('__') ? undefined : item.id,
					type: item.type,
					fillInBy: item.fillInBy,
					title: item.title
						? {
								text: item.title.text.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
						  }
						: undefined,
					paragraph: item.paragraph
						? {
								text: item.paragraph.text.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
						  }
						: undefined,
					stars: item.stars
						? {
								title: item.stars.title.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
						  }
						: undefined,
					openQuestion: item.openQuestion
						? {
								title: item.openQuestion.title.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
						  }
						: undefined,
					sentiment: item.sentiment
						? {
								title: item.sentiment.title.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
								displayMethod: item.sentiment.displayMethod,
						  }
						: undefined,
					multipleChoice: item.multipleChoice
						? {
								title: item.multipleChoice.title.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
								optionsMethod: item.multipleChoice.optionsMethod,
								options: item.multipleChoice.options.map((option) => ({
									text: option.text.map((text) => ({
										languageID: text.languageID,
										text: text.text,
									})),
								})),
						  }
						: undefined,
					competency: item.competency
						? {
								competencyID: item.competency.competency.id,
								weight: item.competency.weight,
						  }
						: undefined,
					finalScore: item.finalScore
						? {
								title: item.finalScore.title?.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
								calculationMethod: item.finalScore.calculationMethod,
								competencyLevelSetID: item.finalScore.competencyLevelSet?.id,
								displayLevelSetID: item.finalScore.displayLevelSet?.id,
								includeReviewGoals: item.finalScore.includeReviewGoals,
						  }
						: undefined,
					signature: item.signature
						? {
								title: item.signature.title?.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
						  }
						: undefined,
					reviewGoals: item.reviewGoals
						? {
								competencyLevelSetID: item.reviewGoals.competencyLevelSet?.id,
						  }
						: undefined,
					developmentPlan: item.developmentPlan
						? {
								title: item.developmentPlan.title?.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
						  }
						: undefined,
				})),
			})),
		};
	};

	const onDuplicate = attempt(async () => {
		const result = await duplicate({
			variables: {
				input: mapInput(true),
			},
		});

		if (result.data) {
			history.push(`/settings/conversations/${result.data.createEvaluationForm.evaluationForm.id}`);
		}
	});

	const onUpdate = attempt(async () => {
		reset();

		await update({
			variables: {
				input: {
					id: props.form.id,
					...mapInput(),
				},
			},
		});

		formik.resetForm({ values: formik.values });

		addToast(t('common.settings.evaluationForms.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	return (
		<Flex flexDirection="column" mt={3}>
			<Prompt
				when={formik.dirty}
				message={t('common.settings.evaluationForms.edit.unsavedChanges')}
			/>

			<Flex flexDirection="row" alignItems="center">
				<PageTitle>{t(`common.settings.evaluationForms.edit.title`)}</PageTitle>
			</Flex>
			<Flex mt={4} mb={2}>
				<SubSectionTitle>{t('common.settings.evaluationForms.edit.details')}</SubSectionTitle>
			</Flex>
			<FormikProvider value={formik}>
				<Form>
					<DetailsTableForm
						formValues={[
							{
								label: t('common.settings.evaluationForms.edit.name'),
								name: 'title',
								type: DetailsTableFormValueTypes.Multilingual,
							},
							{
								label: t('common.settings.evaluationForms.edit.description'),
								name: 'description',
								type: DetailsTableFormValueTypes.MultilingualQuill,
							},
							{
								label: t('common.settings.evaluationForms.edit.showAnswersWhenAllUsersShared'),
								name: 'showAnswersWhenAllUsersShared',
								type: DetailsTableFormValueTypes.Switch,
							},
						]}
					/>
				</Form>

				<Flex mt={4} mb={2}>
					<SubSectionTitle>{t('common.settings.evaluationForms.edit.designer')}</SubSectionTitle>
				</Flex>
				<Preview />
			</FormikProvider>

			<GraphQLErrorMessage error={error} showValidationDetails />

			<Flex mt={3}>
				<Button size={32} onClick={onUpdate} isLoading={loading}>
					{t('common.save')}
				</Button>
			</Flex>

			<Box mb={2} mt={4}>
				<SectionTitle>{t('common.actions')}</SectionTitle>
			</Box>
			<HorizontalRule />
			<Column width={8 / 12}>
				<Box mt={3}>
					<ActionArchiveListItem
						archivableItem={props.form}
						archivableItemName={t('common.settings.evaluationForms.archive.module.name')}
						archivableItemKey="evaluationForm"
						useArchive={graphql.useArchiveEvaluationFormMutation}
						useRestore={graphql.useRestoreEvaluationFormMutation}
					/>

					<ActionListItem
						label={t('common.settings.evaluationForms.duplicate.label')}
						buttonTitle={t('common.settings.evaluationForms.duplicate.buttonTitle')}
						onButtonClick={onDuplicate}
						buttonDisabled={loading || loadingDuplicate || formik.dirty}
						disabledText={t('common.settings.evaluationForms.duplicate.disabled')}
					/>
				</Box>
			</Column>
		</Flex>
	);
}

function EvaluationFormEditorID(props: { id: string }): ReactElement {
	const { t } = useTranslation();

	useBreadcrumb(t('common.settings.evaluationForms.edit.title'));

	const { data, error, loading } = graphql.useEvaluationFormQuery({
		variables: { id: props.id },
		fetchPolicy: 'network-only',
	});

	if (loading) {
		return <Spinner isLoading />;
	} else if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	} else {
		return <EvaluationFormEditor form={data!.evaluationForm as graphql.EvaluationForm} />;
	}
}

export default EvaluationFormEditorID;
